import { create } from "zustand";
import { ColorEntry, defaultChartColors, defaultTableColors } from "@/utils/formatTemplate/formatTemplate";

type FormData = {
  companyName: string;
  templateName: string;
  file: File[] | null;
};

type fontsSection = {
  fontColor: string;
  fontStyle: string;
  fontSize: string;
  placeholderTitle: {
    style: string;
    size: string;
    color: string;
    bold: boolean;
    italic: boolean;
    underline: boolean;
  };
  placeholderSubTitle: {
    style: string;
    size: string;
    color: string;
    bold: boolean;
    italic: boolean;
    underline: boolean;
  };
  slideTitle: {
    style: string;
    size: string;
    color: string;
    bold: boolean;
    italic: boolean;
    underline: boolean;
  };
  slideSubTitle: {
    style: string;
    size: string;
    color: string;
    bold: boolean;
    italic: boolean;
    underline: boolean;
  };
  keyTakeAways: {
    style: string;
    size: string;
    color: string;
    bold: boolean;
    italic: boolean;
    underline: boolean;
  };
  placeholderAxisLabels: {
    style: string;
    size: string;
    color: string;
    bold: boolean;
    italic: boolean;
    underline: boolean;
  };
  source: {
    style: string;
    size: string;
    color: string;
    bold: boolean;
    italic: boolean;
    underline: boolean;
    approach: "Centralized" | "Decentralized";
  };
  footnotes: {
    style: string;
    size: string;
    color: string;
    bold: boolean;
    italic: boolean;
    underline: boolean;
    approach: "Centralized" | "Decentralized";
  };
};

type colorsSection = {
  chartColors: ColorEntry[];
  tableColors: ColorEntry[];
};

type numericalSelection = {
  numericalFormats: string;
  decimalsForSharePrice: string;
  dateFormatForCharts: string;
  percentageValueFormats: string;
  thousandsShort: string;
  millionsShort: string;
  billionsShort: string;
  trillionsShort: string;
  enterpriseValueNaming: string;
  enterpriseValueShort: string;
  equityValueNaming: string;
  equityValueShort: string;
};

type formatTemplateSidebar = "format-template" | "search-existing" | "create-new" | "update" | "account-management";

interface StoreState {
  formatId: string;
  disabledButton: boolean;
  currentStep: number;
  answersByStep: number[];
  questionsPerStep: number[];
  formatTemplateSidebar: formatTemplateSidebar;
  formatTemplateData: FormData;
  fontsSection: fontsSection;
  colorsSection: colorsSection;
  numericalSelection: numericalSelection;
  editApprovals: string[];
  slideImages: string[];
  imagesWithCategory: { [key: string]: string[] }[];
  isLoading: boolean;
  isAllowedToEdit: boolean;
  isDefaultFormat: boolean;
  initialData: any;
  // actions
  setFormatId: (id: string) => void;
  setDisabledButton: (disabled: boolean) => void;
  setCurrentStep: (step: number) => void;
  markAnswered: (step: number) => void;
  updateAnswered: (step: number, value: number) => void;
  nextStep: () => void; // ⬅️ new helper
  prevStep: () => void;
  setFormatTemplateSidebar: (link: formatTemplateSidebar) => void;
  updateFormatTemplateField: <K extends keyof FormData>(key: K, value: FormData[K]) => void;
  updateFontsSection: <K extends keyof fontsSection>(key: K, value: fontsSection[K]) => void;
  updateFontProperty: (section: keyof fontsSection, property: string, value: any) => void;
  updateColorsSection: <K extends keyof colorsSection>(key: K, value: colorsSection[K]) => void;
  updateNumericalSelection: <K extends keyof numericalSelection>(key: K, value: numericalSelection[K]) => void;
  setEditApprovals: (approvals: string[]) => void;
  setSlideImages: (images: string[]) => void;
  updateImageCategory: (imageName: string, category: string) => void;
  setIsloading: (loading: boolean) => void;
  resetStore: () => void;
  setIsAllowedToEdit: (value: boolean) => void;
  setIsDefaultFormat: (value: boolean) => void;
  setInitialData: (data: any) => void;
}

export const useFormatTemplateStore = create<StoreState>((set, get) => {
  return {
    /* ---------- state ---------- */
    formatId: "",
    disabledButton: true,
    currentStep: 0,
    questionsPerStep: [1, 1, 11, 1, 1, 1],
    answersByStep: [0, 0, 0, 1, 0, 0],
    formatTemplateSidebar: "search-existing",
    formatTemplateData: {
      companyName: "",
      templateName: "",
      file: null,
    },
    fontsSection: {
      fontColor: "",
      fontStyle: "",
      fontSize: "",
      placeholderTitle: {
        style: "Arial",
        size: "12",
        color: "rgb(0,0,0)",
        bold: false,
        italic: false,
        underline: false,
      },
      placeholderSubTitle: {
        style: "Arial",
        size: "12",
        color: "rgb(0,0,0)",
        bold: false,
        italic: false,
        underline: false,
      },
      slideTitle: {
        style: "Arial",
        size: "12",
        color: "rgb(0,0,0)",
        bold: false,
        italic: false,
        underline: false,
      },
      slideSubTitle: {
        style: "Arial",
        size: "12",
        color: "rgb(0,0,0)",
        bold: false,
        italic: false,
        underline: false,
      },
      keyTakeAways: {
        style: "Arial",
        size: "12",
        color: "rgb(0,0,0)",
        bold: false,
        italic: false,
        underline: false,
      },
      placeholderAxisLabels: {
        style: "Arial",
        size: "12",
        color: "rgb(0,0,0)",
        bold: false,
        italic: false,
        underline: false,
      },
      source: {
        style: "Arial",
        size: "12",
        color: "rgb(0,0,0)",
        bold: false,
        italic: false,
        underline: false,
        approach: "Centralized",
      },
      footnotes: {
        style: "Arial",
        size: "12",
        color: "rgb(0,0,0)",
        bold: false,
        italic: false,
        underline: false,
        approach: "Centralized",
      },
    },
    colorsSection: {
      chartColors: defaultChartColors,
      tableColors: defaultTableColors,
    },
    numericalSelection: {
      numericalFormats: "#,###;(#,###);-",
      decimalsForSharePrice: "0",
      dateFormatForCharts: "$-en-US]mmm-yy",
      percentageValueFormats: "#,###0%;(#,###0%);-",
      thousandsShort: "000's",
      millionsShort: "MM",
      billionsShort: "Bn",
      trillionsShort: "Tn",
      enterpriseValueNaming: "Aggregate Value",
      enterpriseValueShort: "AV",
      equityValueNaming: "Equity Value",
      equityValueShort: "EV",
    },
    editApprovals: [],
    slideImages: [],
    imagesWithCategory: [],
    isLoading: false,
    isAllowedToEdit: false,
    isDefaultFormat: false,
    initialData: {},

    /* ---------- setters ---------- */
    setFormatId: (id) => set({ formatId: id }),
    setDisabledButton: (disabled) => set({ disabledButton: disabled }),
    setCurrentStep: (step) => set({ currentStep: step }),
    setFormatTemplateSidebar: (link) => set({ formatTemplateSidebar: link }),
    setSlideImages: (images) => set({ slideImages: images }),
    setIsloading: (loading) => set({ isLoading: loading }),
    setIsAllowedToEdit: (value) => set({ isAllowedToEdit: value }),
    setIsDefaultFormat: (value) => set({ isDefaultFormat: value }),
    setInitialData: (data) => set({ initialData: data }),

    /* ---------- business logic ---------- */
    markAnswered: (step) => {
      const { answersByStep, questionsPerStep } = get();
      const next = [...answersByStep];

      next[step] = Math.min(next[step] + 1, questionsPerStep[step]);
      const isStepComplete = next[step] === questionsPerStep[step];

      set({
        answersByStep: next,
        disabledButton: !isStepComplete, // locked until finished
      });
    },

    updateAnswered: (step, value) => {
      const { answersByStep, questionsPerStep } = get();
      const next = [...answersByStep];

      next[step] = value;
      const isStepComplete = next[step] === questionsPerStep[step];

      set({
        answersByStep: next,
        disabledButton: !isStepComplete, // locked until finished
      });
    },

    /** Move forward one step **and** lock/unlock the button
     *  according to the *target* step’s completion state. */
    nextStep: () => {
      const { currentStep, answersByStep, questionsPerStep } = get();
      const targetStep = Math.min(currentStep + 1, questionsPerStep.length - 1);

      const isTargetComplete = answersByStep[targetStep] === questionsPerStep[targetStep];
      set({
        currentStep: targetStep,
        disabledButton: !isTargetComplete, // lock unless target is done
      });
    },

    prevStep: () => {
      const { currentStep, answersByStep, questionsPerStep } = get();
      const targetStep = Math.max(0, currentStep - 1);

      const isTargetComplete = answersByStep[targetStep] === questionsPerStep[targetStep];

      set({
        currentStep: targetStep,
        disabledButton: !isTargetComplete,
      });
    },

    updateFormatTemplateField: (key, value) =>
      set((state) => {
        const updatedData = {
          ...state.formatTemplateData,
          [key]: value,
        };

        const isStepComplete = Boolean(
          updatedData.companyName && updatedData.templateName && updatedData.file?.length === 1
        );

        const updatedAnswers = [...state.answersByStep];
        updatedAnswers[state.currentStep] = isStepComplete ? 1 : 0;

        return {
          formatTemplateData: updatedData,
          answersByStep: updatedAnswers,
          disabledButton: !isStepComplete,
        };
      }),

    updateFontsSection: (key, value) =>
      set((state) => {
        const updatedData = {
          ...state.fontsSection,
          [key]: value,
        };

        // Count filled fields for validation
        const basicFields = [updatedData.fontColor, updatedData.fontStyle, updatedData.fontSize].filter(Boolean).length;
        const detailedFields = [
          updatedData.placeholderTitle.style,
          updatedData.placeholderSubTitle.style,
          updatedData.slideTitle.style,
          updatedData.slideSubTitle.style,
          updatedData.keyTakeAways.style,
          updatedData.placeholderAxisLabels.style,
          updatedData.source.style,
          updatedData.footnotes.style,
        ].filter(Boolean).length;
        
        const totalFilledFields = basicFields + detailedFields;

        const updatedAnswers = [...state.answersByStep];
        updatedAnswers[state.currentStep] = totalFilledFields;

        return {
          fontsSection: updatedData,
          answersByStep: updatedAnswers,
          disabledButton: totalFilledFields < 11, // Updated threshold to 11 for keyTakeAways field
        };
      }),

    updateFontProperty: (section, property, value) =>
      set((state) => {
        const currentSection = state.fontsSection[section];
        if (currentSection && typeof currentSection === "object" && property in currentSection) {
          const updatedSection = {
            ...currentSection,
            [property]: value,
          };
          
          const updatedData = {
            ...state.fontsSection,
            [section]: updatedSection,
          };

          // Count filled fields for validation
          const basicFields = [updatedData.fontColor, updatedData.fontStyle, updatedData.fontSize].filter(Boolean).length;
          const detailedFields = [
            updatedData.placeholderTitle.style,
            updatedData.placeholderSubTitle.style,
            updatedData.slideTitle.style,
            updatedData.slideSubTitle.style,
            updatedData.keyTakeAways.style,
            updatedData.placeholderAxisLabels.style,
            updatedData.source.style,
            updatedData.footnotes.style,
          ].filter(Boolean).length;
          
          const totalFilledFields = basicFields + detailedFields;

          const updatedAnswers = [...state.answersByStep];
          updatedAnswers[state.currentStep] = totalFilledFields;

          return {
            fontsSection: updatedData,
            answersByStep: updatedAnswers,
            disabledButton: totalFilledFields < 11,
          };
        }
        return state;
      }),

    updateColorsSection: (key, value) =>
      set((state) => {
        const updatedData = {
          ...state.colorsSection,
          [key]: value,
        };
        const filledFieldsCount = [updatedData.chartColors.length > 1, updatedData.tableColors.length > 1].filter(
          Boolean
        ).length;
        const updatedAnswers = [...state.answersByStep];
        updatedAnswers[state.currentStep] = filledFieldsCount;

        return {
          colorsSection: updatedData,
          answersByStep: updatedAnswers,
          disabledButton: filledFieldsCount < 2,
        };
      }),

    updateNumericalSelection: (key, value) =>
      set((state) => {
        const updatedData = {
          ...state.numericalSelection,
          [key]: value,
        };
        const updatedAnswers = [...state.answersByStep];
        updatedAnswers[state.currentStep] = 1;

        return {
          numericalSelection: updatedData,
          answersByStep: updatedAnswers,
          disabledButton: false,
        };
      }),

    setEditApprovals: (approvals) =>
      set((state) => {
        const updatedApprovals = [...state.editApprovals, ...approvals];

        const filledFieldsCount = updatedApprovals.filter(Boolean).length;
        const updatedAnswers = [...state.answersByStep];
        updatedAnswers[state.currentStep] = filledFieldsCount;
        return {
          editApprovals: updatedApprovals,
          answersByStep: updatedAnswers,
          disabledButton: filledFieldsCount < 1,
        };
      }),

    updateImageCategory: (imageName, category) =>
      set((state) => {
        const exists = state.imagesWithCategory.some((img: any) => img.imageName === imageName);
        let imagesWithCategory: any[];
        if (exists) {
          imagesWithCategory = state.imagesWithCategory.map((img: any) =>
            img.imageName === imageName ? { ...img, category } : img
          );
        } else {
          imagesWithCategory = [...state.imagesWithCategory, { imageName, category }];
        }
        return {
          imagesWithCategory,
        };
      }),
    resetStore: () =>
      set(() => ({
        formatId: "",
        disabledButton: true,
        currentStep: 0,
        answersByStep: [0, 0, 0, 1, 0, 0],
        questionsPerStep: [1, 1, 11, 1, 1, 1],
        formatTemplateData: {
          companyName: "",
          templateName: "",
          file: null,
        },
        fontsSection: {
          fontColor: "",
          fontStyle: "",
          fontSize: "",
          placeholderTitle: {
            style: "Arial",
            size: "12",
            color: "rgb(0,0,0)",
            bold: false,
            italic: false,
            underline: false,
          },
          placeholderSubTitle: {
            style: "Arial",
            size: "12",
            color: "rgb(0,0,0)",
            bold: false,
            italic: false,
            underline: false,
          },
          slideTitle: {
            style: "Arial",
            size: "12",
            color: "rgb(0,0,0)",
            bold: false,
            italic: false,
            underline: false,
          },
          slideSubTitle: {
            style: "Arial",
            size: "12",
            color: "rgb(0,0,0)",
            bold: false,
            italic: false,
            underline: false,
          },
          keyTakeAways: {
            style: "Arial",
            size: "12",
            color: "rgb(0,0,0)",
            bold: false,
            italic: false,
            underline: false,
          },
          placeholderAxisLabels: {
            style: "Arial",
            size: "12",
            color: "rgb(0,0,0)",
            bold: false,
            italic: false,
            underline: false,
          },
          source: {
            style: "Arial",
            size: "12",
            color: "rgb(0,0,0)",
            bold: false,
            italic: false,
            underline: false,
            approach: "Centralized",
          },
          footnotes: {
            style: "Arial",
            size: "12",
            color: "rgb(0,0,0)",
            bold: false,
            italic: false,
            underline: false,
            approach: "Centralized",
          },
        },
        colorsSection: {
          chartColors: defaultChartColors,
          tableColors: defaultTableColors,
        },
        numericalSelection: {
          numericalFormats: "#,###;(#,###);-",
          decimalsForSharePrice: "0",
          dateFormatForCharts: "$-en-US]mmm-yy",
          percentageValueFormats: "#,###0%;(#,###0%);-",
          thousandsShort: "000's",
          millionsShort: "MM",
          billionsShort: "Bn",
          trillionsShort: "Tn",
          enterpriseValueNaming: "Aggregate Value",
          enterpriseValueShort: "AV",
          equityValueNaming: "Equity Value",
          equityValueShort: "EV",
        },
        editApprovals: [],
        slideImages: [],
        imagesWithCategory: [],
        isLoading: false,
      })),
  };
});
